import { useMemo } from 'react';

import { AppIcon, competencyTransform } from '@backrs_ui/common-ui';

import discussionQuestionsImg from '@backrs_ui/grow-ui/src/assets/images/gpa-guide/discussion-questions.png';
import gpaGuideBanner from '@backrs_ui/grow-ui/src/assets/images/gpa-guide/gpa-guide-banner.png';
import {
  GuidePageShell,
  GuideSections,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { useGetContentNuggetsBySlugsQuery } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import {
  BroaderValueSection,
  CorePrinciplesSection,
  FourYearFrameworkSection,
  MythsVsRealitySection,
  StrategyByGoalSection,
} from './components';
import { useAgentContext } from './GpaGuidePageContainer.agentContext';
import { gpaGuideContent } from '../content';

const SECTION_IDS = {
  overview: 'gpa-guide-section-overview',
  tools: 'gpa-guide-section-tools',
  broaderValue: 'gpa-guide-section-broader-value',
  corePrinciples: 'gpa-guide-section-core-principles',
  myths: 'gpa-guide-section-myths',
  strategy: 'gpa-guide-section-strategy',
  fourYear: 'gpa-guide-section-four-year',
} as const;

const GPA_GUIDE_TOOL_ID = 'gpa_guide';

const GPA_TOOL_IDS: readonly StickerCardId[] = ['are_you_on_track', 'what_are_your_chances'];

const GPA_COMPETENCY_SLUG = 'academics-studying';

const GPA_NUGGET_SLUGS: readonly string[] = [
  'how-to-get-a-40-gpa-in-high-school',
  'retrieval-practice',
  'the-9-best-scientific-study-tips',
  'study-effectively-',
  'how-to-plan-for-exams-and-set-up-an-effective-schedule',
  '5-ways-to-improve-your-sleep-',
];

export const GpaGuidePageContainer = () => {
  useAgentContext();

  const { data: resourceNuggets } = useGetContentNuggetsBySlugsQuery(GPA_NUGGET_SLUGS);
  const { contentCompetencies } = useAppSelector(selectContentCompetencies);

  const studyingNugget = useMemo(() => {
    const competency = contentCompetencies.find((c) => c.slug === GPA_COMPETENCY_SLUG);

    return competency ? competencyTransform(competency) : undefined;
  }, [contentCompetencies]);

  const orderedNuggets = useMemo(() => {
    if (!resourceNuggets) return [];

    const bySlug = new Map(resourceNuggets.map((n) => [n.slug, n]));

    return GPA_NUGGET_SLUGS.map((slug) => bySlug.get(slug)).filter(
      (n): n is NonNullable<typeof n> => Boolean(n),
    );
  }, [resourceNuggets]);

  const toolResourceNuggets = useMemo(
    () => (studyingNugget ? [studyingNugget, ...orderedNuggets] : orderedNuggets),
    [studyingNugget, orderedNuggets],
  );

  const sections = [
    {
      id: SECTION_IDS.broaderValue,
      title: gpaGuideContent.broaderValue.title,
      subtitle: gpaGuideContent.broaderValue.subtitle,
      rightSlot: (
        <LubavButton
          id="gpa-guide-broader-value-lubav-btn"
          label="How does this apply to me?"
          prompt="Based on my profile and goals, which of these broader benefits of a strong GPA matters most for me right now? Consider eligibility for programs, preparation for college, building confidence, developing habits, and unlocking opportunities."
          allowTools={false}
        />
      ),
      content: <BroaderValueSection content={gpaGuideContent.broaderValue} />,
    },
    {
      id: SECTION_IDS.tools,
      title: gpaGuideContent.tools.title,
      subtitle: gpaGuideContent.tools.subtitle,
      rightSlot: (
        <LubavButton
          id="gpa-guide-tools-lubav-btn"
          label="Which tool should I start with?"
          prompt="Which of these tools should I start with to support my GPA and academic goals?"
          allowTools={false}
        />
      ),
      content: <GuideToolsSection toolIds={GPA_TOOL_IDS} extraNuggets={toolResourceNuggets} />,
    },
    {
      id: SECTION_IDS.corePrinciples,
      title: gpaGuideContent.corePrinciples.title,
      subtitle: gpaGuideContent.corePrinciples.subtitle,
      rightSlot: (
        <LubavButton
          id="gpa-guide-core-principles-lubav-btn"
          label="Which matters most for me?"
          prompt="Based on what you know about me, which of the five core GPA principles should I focus on most: (1) Balance Challenge with Mastery, (2) Consistency Over Time, (3) Master the Fundamentals, (4) Plan Ahead, or (5) Seek Help Proactively? Explain why that principle applies to my situation and give me one actionable step."
          allowTools={false}
        />
      ),
      content: (
        <CorePrinciplesSection
          content={gpaGuideContent.corePrinciples}
          storyContent={gpaGuideContent.tellingYourStory}
        />
      ),
    },
    {
      id: SECTION_IDS.fourYear,
      title: gpaGuideContent.fourYearFramework.title,
      subtitle: gpaGuideContent.fourYearFramework.subtitle,
      rightSlot: (
        <LubavButton
          id="gpa-guide-four-year-lubav-btn"
          label="Where am I in this plan?"
          prompt="Based on my profile, what grade am I in and what should I be focusing on right now according to the four-year GPA framework? Give me specific, personalized advice for my current stage and what I should start preparing for next."
        />
      ),
      content: <FourYearFrameworkSection content={gpaGuideContent.fourYearFramework} />,
    },
    {
      id: SECTION_IDS.myths,
      title: gpaGuideContent.mythsVsReality.title,
      subtitle: gpaGuideContent.mythsVsReality.subtitle,
      rightSlot: (
        <LubavButton
          id="gpa-guide-myths-lubav-btn"
          label="Discuss with Lubav"
          prompt="I just read the GPA Common Misconceptions section. Based on what you know about me, identify which misconception I might be most at risk for and explain why. Then tell me what I should do differently."
          allowTools={false}
        />
      ),
      content: <MythsVsRealitySection content={gpaGuideContent.mythsVsReality} />,
    },
    {
      id: SECTION_IDS.strategy,
      title: gpaGuideContent.strategyByGoal.title,
      subtitle: gpaGuideContent.strategyByGoal.subtitle,
      rightSlot: (
        <LubavButton
          id="gpa-guide-strategy-lubav-btn"
          label="Which strategy is best for me?"
          prompt="Based on my profile, goals, and intended path, which GPA strategy category fits me best: college-bound, highly selective, CTE, or scholarship-focused? Give me a personalized version of the advice for my category, tailored to my specific situation."
        />
      ),
      content: <StrategyByGoalSection content={gpaGuideContent.strategyByGoal} />,
    },
  ];

  return (
    <GuidePageShell
      toolId={GPA_GUIDE_TOOL_ID}
      banner={{ src: gpaGuideBanner, alt: 'High School GPA: A Comprehensive Strategy Guide' }}
    >
      <GuideSections
        header={gpaGuideContent.header}
        overview={{
          id: SECTION_IDS.overview,
          extra: (
            <Box sx={discussionCardSx}>
              <Box sx={discussionImgWrapperSx}>
                <Box
                  component="img"
                  src={discussionQuestionsImg}
                  alt="Discussion questions"
                  sx={discussionImgSx}
                />
              </Box>
              <Box sx={discussionContentSx}>
                <Typography variant="text-lg-semibold" sx={discussionTitleSx}>
                  {gpaGuideContent.discussionQuestions.intro}
                </Typography>
                <List disablePadding>
                  {gpaGuideContent.discussionQuestions.questions.map((question) => (
                    <ListItem key={question} disableGutters sx={discussionItemSx}>
                      <ListItemIcon sx={discussionIconSx}>
                        <AppIcon iconName="ArrowRight" diameter="16px" color="currentColor" />
                      </ListItemIcon>
                      <ListItemText
                        primary={question}
                        primaryTypographyProps={{ variant: 'text-sm-regular' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          ),
        }}
        sections={sections}
      />
    </GuidePageShell>
  );
};

const discussionCardSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-xl'],
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  overflow: 'hidden',
  '@container (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
});

const discussionImgWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '180px',
  flexShrink: 0,
  overflow: 'hidden',
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  '@container (min-width: 600px)': {
    width: '300px',
    height: 'auto',
    alignSelf: 'stretch',
    borderBottom: 'none',
    borderRight: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  },
});

const discussionImgSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const discussionContentSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-3xl'],
});

const discussionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  mb: backrs.spacing['spacing-lg'],
});

const discussionItemSx: SxProps<Theme> = ({ backrs }) => ({
  py: backrs.spacing['spacing-xxs'],
  alignItems: 'center',
});

const discussionIconSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 'auto',
  mr: backrs.spacing['spacing-sm'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});
